import Axios from 'axios'

const state = {
  roles: [],
  loading: false,
  error: null,
  message: null
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ROLES(state, roles) {
    state.roles = roles
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_MESSAGE(state, message) {
    state.message = message
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  CLEAR_MESSAGE(state) {
    state.message = null
  },
  ADD_ROLE(state, role) {
    state.roles.push(role)
  },
  UPDATE_ROLE(state, updatedRole) {
    const index = state.roles.findIndex(role => role.roleName === updatedRole.roleName)
    if (index !== -1) {
      state.roles.splice(index, 1, updatedRole)
    }
  },
  REMOVE_ROLE(state, roleName) {
    state.roles = state.roles.filter(role => role.roleName !== roleName)
  }
}

const actions = {
  // Fetch all roles
  async fetchRoles({ commit, rootState }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.get(`${rootState.host}/auth/roles`, { headers })
      commit('SET_ROLES', response.data)
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal memuat data role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Create new role
  async createRole({ commit, rootState }, roleData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.post(`${rootState.host}/auth/roles`, roleData, { headers })
      commit('ADD_ROLE', response.data)
      commit('SET_MESSAGE', 'Role berhasil dibuat')
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal membuat role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Update existing role
  async updateRole({ commit, rootState }, { roleName, roleData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.put(`${rootState.host}/auth/roles/${roleName}`, roleData, { headers })
      commit('UPDATE_ROLE', response.data)
      commit('SET_MESSAGE', 'Role berhasil diupdate')
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal mengupdate role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Delete role
  async deleteRole({ commit, rootState }, roleName) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      await Axios.delete(`${rootState.host}/auth/roles/${roleName}`, { headers })
      commit('REMOVE_ROLE', roleName)
      commit('SET_MESSAGE', 'Role berhasil dihapus')
      commit('SET_LOADING', false)
      return true
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal menghapus role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Get role by name
  async getRoleByName({ commit, rootState }, roleName) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.get(`${rootState.host}/auth/roles/${roleName}`, { headers })
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal mengambil detail role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Toggle role enabled/disabled status
  async toggleRoleEnabled({ commit, rootState }, { roleName, enabled }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.put(`${rootState.host}/auth/roles/${roleName}/enabled`, { enabled }, { headers })
      // Update the role in the state
      const updatedRole = response.data
      commit('UPDATE_ROLE', updatedRole)
      commit('SET_MESSAGE', `Role berhasil ${enabled ? 'diaktifkan' : 'dinonaktifkan'}`)
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal mengubah status role'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // User Role Management Actions
  // Assign role to user
  async assignUserRole({ commit, rootState }, { userId, roleName }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.post(`${rootState.host}/auth/users/${userId}/assign-role/${roleName}`, {}, { headers })
      commit('SET_MESSAGE', 'Role berhasil di-assign ke user')
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal assign role ke user'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Remove role from user
  async removeUserRole({ commit, rootState }, { userId, roleName }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    commit('CLEAR_MESSAGE')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.post(`${rootState.host}/auth/users/${userId}/remove-role/${roleName}`, {}, { headers })
      commit('SET_MESSAGE', 'Role berhasil dihapus dari user')
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal remove role dari user'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  },

  // Get user roles
  async fetchUserRoles({ commit, rootState }, userId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    try {
      const token = localStorage.getItem("token")
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      const response = await Axios.get(`${rootState.host}/auth/users/${userId}/roles`, { headers })
      commit('SET_LOADING', false)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Gagal mengambil role user'
      commit('SET_ERROR', errorMessage)
      commit('SET_LOADING', false)
      throw new Error(errorMessage)
    }
  }
}

const getters = {
  roles: state => state.roles,
  loading: state => state.loading,
  isLoading: state => state.loading,
  error: state => state.error,
  getError: state => state.error,
  message: state => state.message,
  getMessage: state => state.message,
  getRoleByName: (state) => (roleName) => {
    return state.roles.find(role => role.roleName === roleName)
  },
  activeRoles: state => state.roles.filter(role => role.enabled),
  roleCount: state => state.roles.length,
  activeRoleCount: state => state.roles.filter(role => role.enabled).length
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
